/**
 * Toast notification infrastructure.
 *
 * Reusable in-app toast stack for transient user feedback —
 * "Copied to clipboard", "Saved", "Failed, retry?", etc. Lives
 * above the normal page content in a fixed-position stack so
 * multiple toasts can coexist during rapid user actions (e.g.
 * pasting a link several times in a row).
 *
 * # Architecture
 *
 * `<ToastProvider>` is mounted once at the top of the App tree
 * (wraps `<App>`'s authenticated routes). It holds the toast
 * stack in React state + exposes an imperative `showToast(...)`
 * via context. Components that need to trigger a toast call
 * `useToast()` to get the imperative function — avoids passing
 * callbacks through prop trees on every consumer.
 *
 * # Toast lifecycle
 *
 * 1. Consumer calls `useToast().showToast({ kind, message })`.
 * 2. Provider pushes onto its internal array with a unique id.
 * 3. After `DEFAULT_DURATION_MS` (4 s by default, override-able
 *    per toast) a timer removes it from the stack.
 * 4. User can also manually dismiss via the `×` button on each
 *    rendered toast.
 *
 * # Accessibility
 *
 * - The stack container has `role="region"` +
 *   `aria-label="Notifications"` so screen readers announce the
 *   landmark.
 * - Each toast has `role="status"` (polite live region) so new
 *   toasts are announced without interrupting. Critical error
 *   toasts override to `role="alert"` (assertive) when the
 *   caller passes `kind: "error"`.
 * - Dismiss buttons have `aria-label` so screen-reader users
 *   know what they're dismissing.
 *
 * # Visual design
 *
 * - Fixed bottom-right with vertical stacking; fades + slides
 *   in on mount, fades out on dismiss.
 * - Per CONTRIBUTING rule 5 (color + label + glyph): each kind
 *   pairs a distinct color with an emoji glyph + the text
 *   message.
 *   - `success`: green + ✓
 *   - `error`: red + ⚠
 *   - `info`: blue + ℹ
 * - Narrow column (~320 px wide) so toasts don't dominate the
 *   viewport on wide screens; fills-available on mobile.
 *
 * # Why not a third-party lib
 *
 * `react-hot-toast` + `sonner` + `react-toastify` all cover
 * this surface and add ~10-40 KB gzipped. For a ~100 LOC
 * reusable component that fits our design system exactly +
 * plays nicely with Tailwind + avoids a new dependency per
 * CONTRIBUTING §"No new dependencies without operator
 * sign-off," rolling our own is the right call.
 *
 * # Usage
 *
 * ```tsx
 * import { useToast } from "@/lib/toast";
 *
 * function MyComponent() {
 *   const { showToast } = useToast();
 *   const onClick = async () => {
 *     await navigator.clipboard.writeText(url);
 *     showToast({ kind: "success", message: "Link copied" });
 *   };
 *   return <button onClick={onClick}>Copy</button>;
 * }
 * ```
 *
 * And once at App root:
 *
 * ```tsx
 * import { ToastProvider } from "@/lib/toast";
 *
 * export function App() {
 *   return (
 *     <ToastProvider>
 *       <Routes>...</Routes>
 *     </ToastProvider>
 *   );
 * }
 * ```
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { cn } from "@/lib/utils";

// ─── Types ──────────────────────────────────────────────────────────────────

/**
 * Semantic kind of a toast. Drives color + glyph + ARIA live-
 * region role (error → assertive, others → polite).
 */
export type ToastKind = "success" | "error" | "info";

/**
 * One entry in the toast stack. `id` is assigned by the
 * provider; callers supply only `kind` + `message` + optional
 * `durationMs` override.
 */
export interface ToastOptions {
  /**
   * Semantic kind — drives color + glyph + live-region role.
   * Default: `"info"` when omitted.
   */
  kind?: ToastKind;
  /**
   * Short user-facing message. Rendered as plain text — pass
   * raw strings (no JSX) so the screen-reader announcement
   * stays clean. Long messages wrap within the toast's fixed
   * column width; very long messages (>200 chars) get
   * truncated with an ellipsis.
   */
  message: string;
  /**
   * Override the auto-dismiss duration in milliseconds. Pass
   * `0` to disable auto-dismiss entirely (user must manually
   * dismiss). Default: [`DEFAULT_DURATION_MS`].
   */
  durationMs?: number;
  /**
   * Optional action button rendered to the right of the
   * message. When set, the button is the primary affordance:
   * the operator clicks it to invoke the callback (e.g. an
   * Undo flow). The toast auto-dismisses on the same
   * `durationMs` countdown whether or not the action fires.
   *
   * The label is rendered with a live countdown suffix — e.g.
   * `Undo (8s)` — so the operator sees how much window remains
   * before auto-dismiss. The countdown stops when the action
   * is clicked or the toast is dismissed.
   *
   * TRIAG-198: introduced for the unlink → toast(Undo) → relink
   * flow.
   */
  action?: ToastAction;
}

/**
 * Optional action button on a toast. The provider clones this
 * into the [`ToastEntry`] when the toast is registered so
 * dismissing the toast cleans up the closure correctly.
 */
export interface ToastAction {
  /**
   * Button label rendered to the operator. The countdown
   * suffix (` (Ns)`) is appended automatically by the toast
   * renderer based on the toast's `durationMs`; callers
   * should NOT bake the time into the label themselves.
   */
  label: string;
  /**
   * Callback invoked when the operator clicks the button.
   * Should be idempotent / fire-and-forget — the toast
   * dismisses immediately on click; the action runs after.
   */
  onClick: () => void;
}

/**
 * Internal-only toast record — consumers never construct this
 * directly; `showToast` assigns the `id` + normalizes the
 * caller's `ToastOptions`.
 */
interface ToastEntry {
  id: number;
  kind: ToastKind;
  message: string;
  durationMs: number;
  /** TRIAG-198: optional action button. */
  action?: ToastAction;
}

interface ToastContextValue {
  /**
   * Push a new toast onto the stack. Returns the assigned id
   * so the caller can programmatically dismiss early via
   * [`dismissToast`].
   */
  showToast: (options: ToastOptions) => number;
  /**
   * Manually dismiss a toast by id. No-op when the id is not
   * on the stack (e.g. already auto-dismissed).
   */
  dismissToast: (id: number) => void;
}

// ─── Constants ──────────────────────────────────────────────────────────────

/**
 * Default auto-dismiss duration. 4 seconds matches Google's
 * Material Design guideline + is long enough to read a short
 * message + short enough not to feel like a blocker.
 */
const DEFAULT_DURATION_MS = 4_000;

/**
 * Hard cap on visible toasts. Beyond this, oldest-first
 * eviction keeps the stack readable on rapid-fire actions
 * (e.g. copy-link clicked 20 times quickly). Pinning the cap
 * means the stack never crowds the viewport.
 */
const MAX_VISIBLE = 5;

/**
 * Hard cap on message length (characters). Longer messages are
 * truncated with an ellipsis. Keeps toasts scannable + prevents
 * a pathological caller from blowing up the stack with a giant
 * message.
 */
const MESSAGE_MAX_CHARS = 200;

// ─── Context ────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue | null>(null);

// ─── Provider ───────────────────────────────────────────────────────────────

/**
 * Mount once near the top of the component tree. Wraps all
 * components that might want to show a toast; the provider
 * renders the toast stack as a fixed-position overlay so the
 * children's layout is never shifted by toast presence.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  // Monotonically increasing id counter for toast assignment.
  // Using a ref (not state) because we don't want the
  // increment to trigger a re-render — the state change on
  // `setToasts` below is what we actually care about.
  const nextId = useRef(1);
  // Pending auto-dismiss timers indexed by toast id. Cleared
  // on manual dismiss + on provider unmount. Using a ref
  // (not state) because the timer handles are ephemeral UI
  // mechanics, not data the render should depend on.
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(
    new Map(),
  );

  // Core dismiss helper — removes from the stack + clears any
  // pending timer for this id. Stable reference via useCallback
  // so consumer `useEffect([dismissToast])` deps don't churn.
  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const showToast = useCallback(
    (options: ToastOptions): number => {
      const id = nextId.current;
      nextId.current += 1;

      const kind: ToastKind = options.kind ?? "info";
      const durationMs =
        typeof options.durationMs === "number"
          ? options.durationMs
          : DEFAULT_DURATION_MS;

      // Truncate overly-long messages rather than clipping via
      // CSS — so the screen-reader announcement matches the
      // visible text byte-for-byte.
      const message =
        options.message.length > MESSAGE_MAX_CHARS
          ? `${options.message.slice(0, MESSAGE_MAX_CHARS - 1)}…`
          : options.message;

      const entry: ToastEntry = {
        id,
        kind,
        message,
        durationMs,
        action: options.action,
      };

      // Append + enforce MAX_VISIBLE by oldest-first eviction.
      // The evicted entry's timer is cancelled so it doesn't
      // fire a no-op setToasts after the entry is already gone.
      setToasts((prev) => {
        const next = [...prev, entry];
        if (next.length <= MAX_VISIBLE) return next;
        const overflow = next.length - MAX_VISIBLE;
        const evicted = next.slice(0, overflow);
        for (const e of evicted) {
          const t = timersRef.current.get(e.id);
          if (t !== undefined) {
            clearTimeout(t);
            timersRef.current.delete(e.id);
          }
        }
        return next.slice(overflow);
      });

      // Schedule auto-dismiss when durationMs > 0. durationMs=0
      // means "sticky — user must dismiss manually" which is
      // useful for destructive-action confirmations the user
      // shouldn't miss.
      if (durationMs > 0) {
        const timer = setTimeout(() => {
          dismissToast(id);
        }, durationMs);
        timersRef.current.set(id, timer);
      }

      return id;
    },
    [dismissToast],
  );

  // Clear any pending timers on unmount. Without this, a
  // provider unmount mid-toast leaks timers that would later
  // call setState on an unmounted component (React logs a
  // warning). In practice the provider is the app-root and
  // unmounts only on full-page unload, but the cleanup is
  // cheap + defensive.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of timers.values()) {
        clearTimeout(timer);
      }
      timers.clear();
    };
  }, []);

  const contextValue = useMemo<ToastContextValue>(
    () => ({ showToast, dismissToast }),
    [showToast, dismissToast],
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
}

// ─── Hook ───────────────────────────────────────────────────────────────────

/**
 * Consumer hook — returns the imperative `showToast` + `dismissToast`
 * functions. Throws a clear error when used outside a
 * `<ToastProvider>` so missing-wiring bugs surface at render
 * time rather than silently no-op-ing.
 *
 * Both returned functions have stable references across renders
 * (they come from a `useMemo`'d context value), so consumers
 * can safely include them in `useEffect` / `useCallback`
 * dependency arrays without triggering unnecessary re-runs.
 */
// TRIAG-46: canonical useContext hook for the colocated ToastProvider;
// HMR degradation acceptable per eslint.config.js posture.
// eslint-disable-next-line react-refresh/only-export-components -- TRIAG-46: see comment above.
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error(
      "useToast must be used inside a <ToastProvider>. " +
        "Wrap your App component tree with <ToastProvider> " +
        "at the root (see dashboard/src/App.tsx).",
    );
  }
  return ctx;
}

// ─── Stack rendering ────────────────────────────────────────────────────────

function ToastStack({
  toasts,
  onDismiss,
}: {
  toasts: ToastEntry[];
  onDismiss: (id: number) => void;
}) {
  // When the stack is empty, render nothing — no landmark on
  // the a11y tree for "empty region," no pointer-events target
  // in the DOM. Matches the pre-mount state so tab-order +
  // landmark counts are stable whether toasts are visible or
  // not.
  if (toasts.length === 0) {
    return null;
  }

  return (
    <div
      role="region"
      aria-label="Notifications"
      // Fixed bottom-right. `pointer-events-none` on the wrapper
      // so the stack doesn't block clicks through empty gaps;
      // each child toast re-enables pointer events on itself.
      // `z-50` sits above everything in the app's z-stack
      // including modals (which use z-40) — toasts about modal
      // actions need to be visible ABOVE the modal.
      className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-end gap-2 px-4 pb-4 sm:inset-x-auto sm:right-4 sm:w-[min(320px,calc(100vw-2rem))]"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: ToastEntry;
  onDismiss: (id: number) => void;
}) {
  const { kind, message, id, action, durationMs } = toast;

  // Per-kind visual + semantic mapping. Color classes cover
  // both light and dark mode; glyph is the third signal per
  // CONTRIBUTING rule 5. `role="alert"` on errors gets
  // announced assertively (interrupts screen reader); others
  // use `role="status"` (polite — waits for a break).
  const styles = toastKindStyles(kind);
  const role = kind === "error" ? "alert" : "status";

  // TRIAG-198: live countdown suffix on the action button label.
  // Only fires when there's an action AND a finite duration; the
  // sticky `durationMs=0` case skips the countdown entirely.
  // Decrement once per second; clamp to 0. The interval cleans up
  // on unmount + on toast dismissal (the parent stack unmounts
  // this component when the toast leaves the array).
  const showCountdown = action !== undefined && durationMs > 0;
  const [secondsLeft, setSecondsLeft] = useState(
    showCountdown ? Math.ceil(durationMs / 1000) : 0,
  );
  useEffect(() => {
    if (!showCountdown) return;
    const interval = setInterval(() => {
      setSecondsLeft((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [showCountdown]);

  const handleActionClick = () => {
    if (!action) return;
    // Dismiss FIRST (so a slow callback doesn't leave the toast
    // hanging on screen) then invoke. The dismiss removes the
    // toast + clears its auto-dismiss timer.
    onDismiss(id);
    action.onClick();
  };

  return (
    <div
      role={role}
      aria-live={kind === "error" ? "assertive" : "polite"}
      aria-atomic="true"
      className={cn(
        "pointer-events-auto w-full rounded-md border px-3 py-2 text-sm shadow-lg",
        "animate-[fade-in_150ms_ease-out]",
        styles.container,
      )}
    >
      <div className="flex items-start gap-2">
        <span
          aria-hidden="true"
          className={cn("flex-shrink-0 text-base leading-5", styles.glyph)}
        >
          {styles.icon}
        </span>
        <span
          className={cn(
            "flex-1 break-words whitespace-pre-wrap text-sm leading-5",
            styles.text,
          )}
        >
          {message}
        </span>
        {action && (
          <button
            type="button"
            onClick={handleActionClick}
            className={cn(
              "flex-shrink-0 rounded px-2 py-0.5 text-xs font-semibold underline-offset-2 hover:underline",
              "focus-visible:outline-2 focus-visible:outline-offset-2",
              styles.text,
              styles.dismiss,
            )}
          >
            {showCountdown && secondsLeft > 0
              ? `${action.label} (${secondsLeft}s)`
              : action.label}
          </button>
        )}
        <button
          type="button"
          onClick={() => onDismiss(id)}
          aria-label={`Dismiss ${kind} notification`}
          className={cn(
            "-mr-1 -mt-0.5 flex-shrink-0 rounded p-0.5 text-base leading-none transition-opacity",
            "opacity-60 hover:opacity-100 focus-visible:opacity-100",
            "focus-visible:outline-2 focus-visible:outline-offset-2",
            styles.dismiss,
          )}
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
    </div>
  );
}

interface ToastKindStyles {
  icon: string;
  container: string;
  glyph: string;
  text: string;
  dismiss: string;
}

/**
 * Per-kind style + icon table. Extracted so the `ToastItem`
 * render stays declarative + a future kind (e.g. `"warning"`
 * amber) can land by adding one match arm.
 */
function toastKindStyles(kind: ToastKind): ToastKindStyles {
  switch (kind) {
    case "success":
      return {
        icon: "✓",
        container:
          "border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950",
        glyph: "text-emerald-600 dark:text-emerald-300",
        text: "text-emerald-900 dark:text-emerald-100",
        dismiss:
          "text-emerald-700 hover:bg-emerald-100 focus-visible:outline-emerald-600 dark:text-emerald-300 dark:hover:bg-emerald-900",
      };
    case "error":
      return {
        icon: "⚠",
        container:
          "border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950",
        glyph: "text-red-600 dark:text-red-300",
        text: "text-red-900 dark:text-red-100",
        dismiss:
          "text-red-700 hover:bg-red-100 focus-visible:outline-red-600 dark:text-red-300 dark:hover:bg-red-900",
      };
    case "info":
    default:
      return {
        icon: "ℹ",
        container:
          "border-blue-300 bg-blue-50 dark:border-blue-800 dark:bg-blue-950",
        glyph: "text-blue-600 dark:text-blue-300",
        text: "text-blue-900 dark:text-blue-100",
        dismiss:
          "text-blue-700 hover:bg-blue-100 focus-visible:outline-blue-600 dark:text-blue-300 dark:hover:bg-blue-900",
      };
  }
}
